import _objectWithoutPropertiesLoose from "@babel/runtime/helpers/esm/objectWithoutPropertiesLoose";
const _excluded = ["propertyName"];
import Raven from 'raven-js';
import { isMultienum } from 'customer-data-objects/property/PropertyIdentifier';
import { ASSOCIATION, PROPERTY } from 'customer-data-objects/field/FieldTypes';
import { getPipelinePropertyName, getPipelineStagePropertyName } from '../input/pipeline/utils/pipelineUtils';
import { LABEL_REQUIRED } from '../constants/ConditionalAssociations';
import { createConditionEvaluator } from '../conditionalLogic/conditionEvaluator';
const isConditionalPropertyRuleActive = ({
  evaluator,
  isMultiEnum,
  objectTypeId,
  rule
}) => {
  if (objectTypeId !== rule.objectTypeId) {
    return false;
  }

  // Today, we only support simple conditions with the EQUAL operator. But,
  // the multienum behavior we have is better represented by the ANY_OF operator going forward.
  // We will have to do some backfill work here to convert this as part of the migration.
  //
  // For now, we will force specific operators to create a migration path where we
  // hardcode the operator based on the property type.
  const operator = isMultiEnum ? 'ANY_OF' : 'EQUAL';
  return evaluator.evaluateCondition({
    operator,
    fieldName: rule.controllingField.fieldName,
    fieldValues: [rule.controllingField.fieldValue]
  });
};
export const getConditionalPropertyDependencies = (objectTypeId, getFormValue, properties, conditionalPropertyRules, changedProperties, isAutosaving) => {
  const activeRules = {};
  const evaluator = createConditionEvaluator({
    getValue: getFormValue,
    properties
  });
  for (const rule of conditionalPropertyRules) {
    const active = isConditionalPropertyRuleActive({
      evaluator,
      isMultiEnum: isMultienum(properties[rule.controllingField.fieldName]),
      rule,
      objectTypeId
    });
    if (active) {
      const controllingFieldName = rule.controllingField.fieldName;
      activeRules[controllingFieldName] = (Object.prototype.hasOwnProperty.call(activeRules, controllingFieldName) ? activeRules[controllingFieldName] : []).concat(rule.dependentFields);
    }
  }

  // This guards against a bug which is very hard to fix cleanly with our current systems.
  // https://git.hubteam.com/HubSpot/ProductSupport/issues/105907
  // When changing a pipeline property in an autosaving inline properties form, the pipeline modal
  // allows selecting a deal stage as well. That deal stage will be saved before we have a chance
  // to block it via the handlers in the properties form. In order to prevent this, we inject a fake
  // dependent field representing the pipeline stage that will be shown to the user next to the pipeline,
  // and prevent both properties from saving until the pipeline stage can be saved.
  const pipelinePropertyName = getPipelinePropertyName(objectTypeId);
  const pipelineStagePropertyName = getPipelineStagePropertyName(objectTypeId);
  // This behavior is only needed if the customer has a rule configured for the pipeline stage property
  // that needs to be enforced.
  const pipelineStageRule = activeRules[pipelineStagePropertyName];
  if (isAutosaving && changedProperties.has(pipelinePropertyName) && changedProperties.has(pipelineStagePropertyName) && pipelineStageRule) {
    activeRules[pipelinePropertyName] = activeRules[pipelinePropertyName] || [];
    const hasExistingDependentField = activeRules[pipelinePropertyName].some(({
      fieldName
    }) => fieldName === pipelineStagePropertyName);
    // Ignore it if the customer already has a rule that would prevent the bug, otherwise put the fake rule
    // at the front of the array so it will be shown close to the pipeline property.
    if (!hasExistingDependentField) {
      activeRules[pipelinePropertyName].unshift({
        fieldName: pipelineStagePropertyName,
        required: false,
        displayOrder: 0
      });
    }
  }
  return activeRules;
};
export const toFormNode = property => {
  const {
      propertyName
    } = property,
    propertyFields = _objectWithoutPropertiesLoose(property, _excluded);
  return Object.assign({}, propertyFields, {
    fieldName: propertyName,
    fieldType: PROPERTY,
    nodeId: propertyName,
    isRequired: Boolean(property.isRequired),
    dependents: [],
    parent: null
  });
};
const getNodeId = (parentNodeId, childPropertyName) => {
  return `${parentNodeId}/${childPropertyName}`;
};
const getRootNode = node => {
  while (node.parent) {
    node = node.parent;
  }
  return node;
};
const cloneFormNode = node => {
  return Object.assign({}, node, {
    dependents: []
  });
};
export const getFieldNameFromAssociationId = id => {
  const parts = id.split('/');
  if (parts.length < 2) {
    Raven.captureException(new Error(`Invalid association id format: ${id}`));
    return null;
  }
  return parts[1];
};
export const parseAssociationFieldName = fieldName => {
  const parts = fieldName.split(':');
  if (parts.length < 3) {
    Raven.captureException(new Error(`Invalid association fieldName format: ${fieldName}`));
    return null;
  }
  return {
    defaultAssociationIdentifier: parts[0],
    associationLabelIdentifier: parts[1],
    labelRequired: parts[2],
    isLabelRequired: parts[2] === LABEL_REQUIRED,
    displayOrder: parseInt(parts[3], 10)
  };
};

/**
 * Builds the structure for showing nested properties within the form.
 * The output is a tree where the following conditions hold:
 *   - Each initial property in the form is the root of a branch that contains
 *     a node for each dependent property. This is recursive, but duplicates
 *     are pruned per-branch to prevent cycles.
 *   - Conditional rules are applied to any top-level properties that have been
 *     changed, any nested properties, or all properties if `alwaysShowConditionalProperties`
 *     is flagged to true.
 *   - If a dependent property would appear twice, prefer the closest to root
 *
 * General strategy to build:
 *   - Level-order traversal of the dependency tree
 *   - Treat each initial form property as their own branch and keep
 *     set of visited nodes per branch. Evaluate rules of already seen nodes
 *     once, but do not add them to queue.
 *   - Keep track of any conditional rules in a global set while building.
 *     Apply rules as a single pass through tree after all nodes evaluated.
 */
export const applyConditionalPropertyRules = (nodes, dependencies, changedProperties, alwaysShowConditionalPropertyOptionsWithErrors) => {
  const queues = [];
  const visited = [];
  const requiredProperties = new Set();
  const newNodes = nodes.map(cloneFormNode);
  for (const node of newNodes) {
    queues.push([node]);
    visited.push(new Set());
  }
  let depth = 1; // we manually traverse depth = 0 (aka the root) above
  while (queues.some(branch => branch.length > 0)) {
    for (let branchIndex = 0; branchIndex < queues.length; branchIndex++) {
      const level = queues[branchIndex];
      const branchVisited = visited[branchIndex];
      const nextLevel = [];
      for (const node of level) {
        const fieldName = node.fieldName;
        if (node.isRequired) {
          requiredProperties.add(fieldName);
        }
        if (branchVisited.has(fieldName)) {
          continue;
        }
        if (node.parent) {
          node.parent.dependents.push(node);
        }
        if (alwaysShowConditionalPropertyOptionsWithErrors || depth > 1 || changedProperties.has(fieldName)) {
          const dependents = (dependencies[fieldName] || []).reduce((acc, dependent) => {
            const {
              fieldType,
              fieldName: dependentFieldName,
              required,
              displayOrder
            } = dependent;
            const isAssociation = fieldType === ASSOCIATION;
            if (isAssociation) {
              const parsedFieldName = parseAssociationFieldName(dependentFieldName);
              if (!parsedFieldName) {
                return acc;
              }
              const {
                defaultAssociationIdentifier,
                labelRequired,
                isLabelRequired
              } = parsedFieldName;
              // Do NOT check for associationLabelIdentifier here.
              // Pre-CHIRP migration, associationLabelIdentifier can be an empty string for the "No Label" case
              // Post-CHIRP migration, associationLabelIdentifier will be optional and not returned for the "No Label" case
              if (defaultAssociationIdentifier && labelRequired) {
                acc.push({
                  nodeId: getNodeId(node.nodeId, dependentFieldName),
                  fieldName: `${dependentFieldName}:${displayOrder}`,
                  fieldType: ASSOCIATION,
                  isRequired: required,
                  dependents: [],
                  parent: node,
                  isLabelRequired
                });
              }
            } else {
              // isProperty
              acc.push({
                nodeId: getNodeId(node.nodeId, dependentFieldName),
                fieldName: dependentFieldName,
                fieldType: PROPERTY,
                isRequired: required,
                dependents: [],
                parent: node
              });
            }
            return acc;
          }, []);
          nextLevel.push(...dependents);
        }
        branchVisited.add(fieldName);
      }
      queues[branchIndex] = nextLevel;
    }
    depth++;
  }
  const markRequired = node => {
    node.isRequired = requiredProperties.has(node.fieldName);
    node.dependents.forEach(markRequired);
  };
  newNodes.forEach(markRequired);
  return newNodes;
};

// injects conditional property options dependents and builds the tree using applyConditionalPropertyRules
export const applyConditionalPropertyOptionsRules = ({
  nodes,
  formDependencies,
  changedProperties,
  alwaysShowConditionalPropertyOptionsWithErrors,
  conditionalPropertyOptionsRules
}) => {
  const formDependenciesWithCPO = Object.assign({}, formDependencies);
  for (const activeConditionalPropertyOptionsRule of conditionalPropertyOptionsRules) {
    const controllingFieldName = activeConditionalPropertyOptionsRule.controllingField.fieldName;
    const dependentFieldName = activeConditionalPropertyOptionsRule.dependentFieldName;
    const dependentField = {
      fieldName: dependentFieldName,
      required: false,
      // this is a best case scenario. There could be a display order greater than array length
      // but it will not have an impact because we are always pushing CPO dependents to the end
      // since we are recreating CP dependencies on rerenders, CPO dependents will not sneak into CP dependents
      displayOrder: formDependenciesWithCPO[controllingFieldName] ? formDependenciesWithCPO[controllingFieldName].length : 0
    };

    // there are no dependents for this controlling field, so we create a new dependencies array
    if (!Object.prototype.hasOwnProperty.call(formDependenciesWithCPO, controllingFieldName)) {
      formDependenciesWithCPO[controllingFieldName] = [dependentField];
      continue;
    }

    // the dependent field already exists as a dependent for this controlling field.
    // we skip adding to avoid duplicates
    if (formDependenciesWithCPO[controllingFieldName].some(dependent => dependent.fieldName === dependentFieldName)) {
      continue;
    }

    // we push the new dependent into the existing array
    formDependenciesWithCPO[controllingFieldName].push(dependentField);
  }
  return applyConditionalPropertyRules(nodes, formDependenciesWithCPO, changedProperties, alwaysShowConditionalPropertyOptionsWithErrors);
};
export const traverseNodes = (nodes, callback, strategy = 'pre-order') => {
  const traverse = (currentNodes, index) => {
    if (strategy === 'pre-order') {
      for (const node of currentNodes) {
        callback(node, index);
        traverse(node.dependents, index + 1);
      }
      return;
    } else if (strategy === 'post-order') {
      for (const node of currentNodes) {
        traverse(node.dependents, index + 1);
        callback(node, index);
      }
      return;
    }
    const assertNever = strategy;
    throw new Error(`Invalid strategy: ${assertNever}`);
  };
  traverse(nodes, 0);
};
export const mapNodes = (nodes, fn, strategy) => {
  const result = [];
  traverseNodes(nodes, (node, index) => result.push(fn(node, index)), strategy);
  return result;
};
export const filterNodes = (nodes, predicate, strategy) => {
  const result = [];
  traverseNodes(nodes, (node, index) => {
    if (predicate(node, index)) {
      result.push(node);
    }
  }, strategy);
  return result;
};

/**
 * Returns all root properties for a given targetProperty name. For example, if the form looks like this:
 * - A
 *   - B
 *     - C
 * - D
 *   - C
 * - C
 *
 * And the targetProperty is 'C', then the result will be ['A', 'D', 'C']
 */
export const getAllPropertyRoots = (nodes, targetProperty) => {
  return filterNodes(nodes, node => node.fieldName === targetProperty).map(node => getRootNode(node).fieldName);
};
export const getFormFieldNames = nodes => {
  return new Set(mapNodes(nodes, node => node.fieldName));
};

/** returns additional values in first set */
export function difference(a, b) {
  return [...a].filter(x => !b.has(x));
}
const emptyFormPropertiesDifference = {
  added: [],
  removed: []
};
export const getFormFieldsDifference = (prev, next) => {
  if (prev === next) {
    return emptyFormPropertiesDifference;
  }
  const prevFieldNames = getFormFieldNames(prev);
  const nextFieldNames = getFormFieldNames(next);
  return {
    added: difference(nextFieldNames, prevFieldNames),
    removed: difference(prevFieldNames, nextFieldNames)
  };
};

/**
 * Flattens and returns a pre-order traversal of the graph, omitting
 * the initial properties (the first layer). Only dependent fields
 * (and their dependents, etc.) are returned.
 */
export const getDependentFormFields = nodes => {
  return filterNodes(nodes, node => node.parent !== null);
};
export const isAssociationField = field => {
  return 'fieldType' in field && field.fieldType === ASSOCIATION;
};
export const isAssociationFormNode = node => {
  return 'fieldType' in node && node.fieldType === ASSOCIATION;
};
export const getToObjectTypeIdsFromConditionalRules = (conditionalPropertyRules, associationDefinitionMap) => {
  return conditionalPropertyRules.reduce((acc, rule) => {
    rule.dependentFields.forEach(dependentField => {
      if (dependentField.fieldType === ASSOCIATION) {
        const parsedFieldName = parseAssociationFieldName(dependentField.fieldName);
        if (parsedFieldName) {
          const {
            defaultAssociationIdentifier
          } = parsedFieldName;
          const defaultAssociationDefinition = associationDefinitionMap[defaultAssociationIdentifier];
          const toObjectTypeId = defaultAssociationDefinition === null || defaultAssociationDefinition === void 0 ? void 0 : defaultAssociationDefinition.toObjectTypeId;
          if (toObjectTypeId && !acc.includes(toObjectTypeId)) {
            acc.push(toObjectTypeId);
          }
        }
      }
    });
    return acc;
  }, []);
};
export const getIsDefaultAssociationField = fieldName => {
  const parsedFieldName = parseAssociationFieldName(fieldName);
  if (parsedFieldName) {
    const {
      defaultAssociationIdentifier,
      associationLabelIdentifier
    } = parsedFieldName;
    return associationLabelIdentifier === '' || defaultAssociationIdentifier === associationLabelIdentifier;
  }
};
export const getCachedAssociationValuesForField = ({
  fieldName,
  cachedAssociationValues
}) => {
  const parsedFieldName = parseAssociationFieldName(fieldName);
  if (parsedFieldName) {
    const {
      defaultAssociationIdentifier,
      associationLabelIdentifier
    } = parsedFieldName;
    const defaultAssociationValues = cachedAssociationValues[defaultAssociationIdentifier];
    const labeledValues = cachedAssociationValues[associationLabelIdentifier];
    if (getIsDefaultAssociationField(fieldName) && defaultAssociationValues) {
      return {
        value: {
          objectIds: defaultAssociationValues.value,
          labelIdentifier: null
        },
        hasMore: defaultAssociationValues.hasMore
      };
    } else if (associationLabelIdentifier && labeledValues) {
      return {
        value: {
          objectIds: labeledValues.value,
          labelIdentifier: associationLabelIdentifier
        },
        hasMore: labeledValues.hasMore
      };
    }
  }
  return {
    value: null,
    hasMore: false
  };
};
export const getEffectiveLabelIdentifier = (defaultAssociationIdentifier, associationLabelIdentifier, currentLabelIdentifier) => {
  const shouldUpdateLabel = defaultAssociationIdentifier !== associationLabelIdentifier && (currentLabelIdentifier || associationLabelIdentifier);
  return currentLabelIdentifier || (shouldUpdateLabel ? associationLabelIdentifier : null);
};
export const getAssociationsWithExistingAssociationsFiltered = (cachedAssociationValues, defaultAssociationIdentifier, associationLabelIdentifier, currentLabelIdentifier, objectIds) => {
  var _cachedAssociationVal, _cachedAssociationVal2;
  const labelIdentifier = getEffectiveLabelIdentifier(defaultAssociationIdentifier, associationLabelIdentifier, currentLabelIdentifier);
  const existingAssociations = (((_cachedAssociationVal = cachedAssociationValues[defaultAssociationIdentifier]) === null || _cachedAssociationVal === void 0 ? void 0 : _cachedAssociationVal.value) || []).concat(labelIdentifier ? ((_cachedAssociationVal2 = cachedAssociationValues[labelIdentifier]) === null || _cachedAssociationVal2 === void 0 ? void 0 : _cachedAssociationVal2.value) || [] : []);
  return {
    labelIdentifier,
    objectIds: (objectIds === null || objectIds === void 0 ? void 0 : objectIds.filter(toObjectId => !existingAssociations.includes(toObjectId))) || null
  };
};